import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileText, Printer, RefreshCw, Search, Zap, Hand, CreditCard, Hash } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { StatCard } from './StatCard';
import { RecargasAlertas } from './RecargasAlertas';
import { formatDateOnlyBR, formatDateTimeBR } from '../lib/dateFormat';
import { brl, TIPO_LABEL, MODO_LABEL, exportarRecargasCsv, exportarRecargasPdf, imprimirRecargas } from '../lib/recargasExport';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const esteMes = () => { const h = new Date(); return [iso(new Date(h.getFullYear(), h.getMonth(), 1)), iso(h)]; };
const mesPassado = () => { const h = new Date(); return [iso(new Date(h.getFullYear(), h.getMonth() - 1, 1)), iso(new Date(h.getFullYear(), h.getMonth(), 0))]; };
const MODO_COR = { automatica: 'text-blue-300 bg-blue-500/15 border-blue-500/40', manual: 'text-amber-300 bg-amber-500/15 border-amber-500/40' };

export function RecargasTip() {
  const [[inicio, fim], setPeriodo] = useState(esteMes());
  const [dados, setDados] = useState(null);
  const [sync, setSync] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [busca, setBusca] = useState('');
  const rodava = useRef(false);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    if (!inicio || !fim) return undefined;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/recargas`, { params: { inicio, fim }, withCredentials: true, signal: ctrl.signal })
        .then(r => setDados(r.data))
        .catch(e => { if (!axios.isCancel(e)) toast.error(e.response?.data?.detail || 'Erro ao carregar recargas'); });
    }, 400);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [inicio, fim, versao]);
  const recarregar = useCallback(() => setVersao(v => v + 1), []);

  const carregarSync = useCallback(() => axios.get(`${API_URL}/api/recargas/sincronizacao`, { withCredentials: true }).then(r => {
    if (rodava.current && !r.data.rodando) {
      toast.success(`Busca na Tip concluída: ${r.data.novas ?? 0} lançamento(s) novo(s)`);
      recarregar();
    }
    rodava.current = !!r.data.rodando;
    setSync(r.data);
  }).catch(() => {}), [recarregar]);

  useEffect(() => { carregarSync(); }, [carregarSync]);
  useEffect(() => {
    if (!sync?.rodando) return undefined;
    const t = setInterval(carregarSync, 5000);
    return () => clearInterval(t);
  }, [sync?.rodando, carregarSync]);

  const buscarNaTip = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/recargas/sincronizar`, { inicio, fim }, { withCredentials: true });
      toast.success(r.data.message);
      rodava.current = true;
      setSync(s => ({ ...(s || {}), rodando: true }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao buscar na Tip'); }
  };

  const itens = useMemo(() => {
    const s = busca.trim().toLowerCase();
    return (dados?.itens || []).filter(i => (!filtro || `${i.tipo}_${i.modo}` === filtro)
      && (!s || [i.cliente_nome, i.cpf, i.numero, i.iccid].some(v => (v || '').toLowerCase().includes(s))));
  }, [dados, filtro, busca]);
  const totalFiltrado = itens.reduce((s, i) => s + (i.custo || 0), 0);
  const r = dados?.resumo;

  return (
    <div className="space-y-4" data-testid="recargas-tip">
      <div className="rounded-sm border border-zinc-800 bg-zinc-950 p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-zinc-400">Início
            <Input type="date" value={inicio} onChange={e => setPeriodo([e.target.value, fim])} className="form-input mt-1" data-testid="recargas-inicio" />
          </label>
          <label className="text-xs text-zinc-400">Término
            <Input type="date" value={fim} onChange={e => setPeriodo([inicio, e.target.value])} className="form-input mt-1" data-testid="recargas-fim" />
          </label>
          <Button variant="outline" size="sm" className="border-zinc-700" onClick={() => setPeriodo(esteMes())} data-testid="recargas-este-mes">Este mês</Button>
          <Button variant="outline" size="sm" className="border-zinc-700" onClick={() => setPeriodo(mesPassado())} data-testid="recargas-mes-passado">Mês passado</Button>
          <div className="flex-1" />
          <Button onClick={buscarNaTip} disabled={sync?.rodando} className="btn-primary" data-testid="recargas-buscar-tip">
            <RefreshCw className={`w-4 h-4 mr-2 ${sync?.rodando ? 'animate-spin' : ''}`} /> {sync?.rodando ? `Buscando na Tip... ${sync.progresso ? `${sync.progresso} linhas` : ''}` : 'Atualizar da Tip'}
          </Button>
        </div>
        <p className="text-xs text-zinc-500" data-testid="recargas-status-sync">
          {sync?.ultima_execucao ? `Última busca na Tip: ${formatDateTimeBR(sync.ultima_execucao)} (${sync.periodo}, ${sync.linhas} linhas${sync.erros ? `; ${sync.erros} consulta(s) sem dados, normalmente linhas canceladas` : ''}).` : 'Ainda não foi feita nenhuma busca na Tip.'}
          {' '}Atualiza sozinho todo dia às 3h da madrugada.
        </p>
        {sync?.erro && !sync?.rodando && <p className="text-xs text-amber-300" data-testid="recargas-erro-sync">Atenção: {sync.erro}</p>}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Hash} label="Total a pagar à Tip" value={brl(r?.total.total)} color="red" testId="recargas-total" sub={`${r?.total.quantidade ?? 0} lançamento(s)`} />
        <StatCard icon={CreditCard} label="Cargas de ativação" value={brl(r?.ativacoes.total)} color="orange" testId="recargas-ativacoes"
          sub={`${r?.ativacoes.quantidade ?? 0} (${r?.ativacoes_automaticas.quantidade ?? 0} auto · ${r?.ativacoes_manuais.quantidade ?? 0} manual)`} />
        <StatCard icon={Zap} label="Recargas automáticas" value={brl(r?.recargas_automaticas.total)} color="blue" testId="recargas-automaticas" sub={`${r?.recargas_automaticas.quantidade ?? 0} recarga(s)`} />
        <StatCard icon={Hand} label="Recargas manuais" value={brl(r?.recargas_manuais.total)} color="amber" testId="recargas-manuais" sub={`${r?.recargas_manuais.quantidade ?? 0} recarga(s)`} />
      </div>
      {r?.sem_custo.quantidade > 0 && (
        <p className="text-xs text-amber-300" data-testid="recargas-sem-custo">{r.sem_custo.quantidade} lançamento(s) sem custo: cadastre o custo da oferta em "Ofertas Detalhadas".</p>
      )}
      {r?.plano_sem_oferta?.quantidade > 0 && (
        <p className="text-xs text-amber-300" data-testid="recargas-plano-sem-oferta">
          {r.plano_sem_oferta.quantidade} recarga(s) num plano da Tip que não está ligado a nenhuma oferta ({r.plano_sem_oferta.planos.join(', ')}). Nelas usei o custo da oferta da linha.
        </p>
      )}
      {dados && <RecargasAlertas alertas={dados.alertas} />}

      <div className="flex flex-col sm:flex-row gap-3">
        <select value={filtro} onChange={e => setFiltro(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm" data-testid="recargas-filtro">
          <option value="">Todos os tipos</option>
          <option value="ativacao_automatica">Ativação automática (QR code)</option>
          <option value="ativacao_manual">Ativação manual</option>
          <option value="recarga_automatica">Recarga automática</option>
          <option value="recarga_manual">Recarga manual</option>
        </select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar cliente, CPF, número ou ICCID..." className="form-input pl-10" data-testid="recargas-busca" />
        </div>
        <Button variant="outline" className="border-zinc-700" disabled={!itens.length} onClick={() => exportarRecargasCsv(itens, dados.inicio, dados.fim)} data-testid="recargas-exportar-csv">
          <Download className="w-4 h-4 mr-2" /> CSV
        </Button>
        <Button variant="outline" className="border-zinc-700" disabled={!itens.length}
          onClick={() => exportarRecargasPdf(dados, itens).catch(() => toast.error('Erro ao gerar o PDF'))} data-testid="recargas-exportar-pdf">
          <FileText className="w-4 h-4 mr-2" /> PDF
        </Button>
        <Button variant="outline" className="border-zinc-700" disabled={!itens.length} onClick={() => imprimirRecargas(dados, itens) || toast.error('Libere as janelas pop-up do navegador para imprimir')} data-testid="recargas-imprimir">
          <Printer className="w-4 h-4 mr-2" /> Imprimir
        </Button>
      </div>
      <TabelaRecargas itens={itens} total={totalFiltrado} carregando={!dados} />
    </div>
  );
}

function TabelaRecargas({ itens, total, carregando }) {
  if (carregando) return <p className="text-sm text-zinc-400">Carregando...</p>;
  if (!itens.length) return <p className="text-sm text-zinc-400" data-testid="recargas-vazio">Nenhuma carga ou recarga neste período. Clique em "Atualizar da Tip" para buscar.</p>;
  return (
    <div className="rounded-sm border border-zinc-800 overflow-x-auto">
      <table className="w-full text-sm" data-testid="recargas-tabela">
        <thead className="bg-zinc-900 text-xs uppercase text-zinc-400">
          <tr>{['Cliente', 'Número', 'Tipo', 'Modo', 'Data', 'Hora', 'Plano na Tip / Oferta', 'Custo'].map(h => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr>
        </thead>
        <tbody>
          {itens.map(i => (
            <tr key={i.id} className="border-t border-zinc-800" data-testid={`recarga-${i.id}`}>
              <td className="px-3 py-2">{i.cliente_nome}<div className="text-xs text-zinc-500 font-mono">{i.cpf}</div></td>
              <td className="px-3 py-2 font-mono">{i.numero}</td>
              <td className="px-3 py-2">{TIPO_LABEL[i.tipo]}</td>
              <td className="px-3 py-2"><span title={i.motivo} className={`px-2 py-0.5 rounded border text-xs ${MODO_COR[i.modo]}`}>{MODO_LABEL[i.modo]}</span>
                <div className="text-[11px] text-zinc-500 mt-0.5">{i.motivo}{i.fonte === 'sistema' && ' · registro do sistema (a Tip não listou)'}</div></td>
              <td className="px-3 py-2 whitespace-nowrap">{formatDateOnlyBR(i.data)}</td>
              <td className="px-3 py-2 font-mono">{i.hora || '—'}</td>
              <td className="px-3 py-2">{i.plano_tip || '—'}<div className="text-xs text-zinc-500">{i.oferta_nome}</div></td>
              <td className="px-3 py-2 whitespace-nowrap">{brl(i.custo)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-zinc-900 font-semibold">
          <tr><td className="px-3 py-2" colSpan={7}>Total ({itens.length} lançamentos)</td><td className="px-3 py-2" data-testid="recargas-total-tabela">{brl(total)}</td></tr>
        </tfoot>
      </table>
    </div>
  );
}
